import {
  Dispatch, SetStateAction, useContext, useEffect, useState,
} from 'react';

import { certificatesApi, gamificationApi } from '../api';
import { AppDataContext } from '../context';
import {
  CertificateDisplayItem, CertificateListItemResponse, GamificationData, UserBadge,
} from '../types';

interface UseUserAchievements {
  username: string;
  setEnabled: Dispatch<SetStateAction<boolean>>;
  displayCertificates: boolean;
}

export const useUserAchievements = ({
  username,
  setEnabled,
  displayCertificates,
}: UseUserAchievements): { userCertificates: CertificateDisplayItem[]; userBadges: UserBadge[] } => {
  const [allUserCertificates, setAllUserCertificates] = useState<CertificateListItemResponse[]>([]);
  const [certificatesLoaded, setCertificatesLoaded] = useState<boolean>(false);
  const [userBadges, setUserBadges] = useState<UserBadge[]>([]);
  const [userCertificates, setUserCertificates] = useState<CertificateDisplayItem[]>([]);

  const { allowedCourses } = useContext(AppDataContext);

  useEffect(() => {
    if (displayCertificates && allowedCourses.length) {
      certificatesApi.getUserCertificates(username, (res: CertificateListItemResponse[]) => {
        setAllUserCertificates(res || []);
        setCertificatesLoaded(true);
      });
    }
  }, [username, displayCertificates, allowedCourses]);

  useEffect(() => {
    if (!certificatesLoaded || !allowedCourses.length) {
      return;
    }
    const certById = new Map(allUserCertificates.map((cert) => [cert.course_id, cert] as const));
    const list: CertificateDisplayItem[] = allowedCourses.flatMap<CertificateDisplayItem>((course) => {
      if (!course.display_certificate_badge) {
        return [];
      }
      const earned = certById.get(course.course_id);
      return [{
        course_id: course.course_id,
        course_display_name:
          earned?.course_display_name
          || course.certificate_badge_name
          || course.certificate_display_name
          || course.course_id,
        iconImage: course.certificate_badge_url,
        uuid: earned?.uuid,
        isEarned: !!earned,
      }];
    });
    setEnabled(list.length > 0);
    setUserCertificates(list);
  }, [allUserCertificates, certificatesLoaded, allowedCourses]);

  useEffect(() => {
    gamificationApi.getGamificationData((res: GamificationData) => {
      setUserBadges(res?.badges || []);
    });
  }, [username]);

  return {
    userBadges,
    userCertificates,
  };
};
